import { FC, useEffect, useRef, useState } from "react";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Check, Copy } from "@/icons/outlined";

const COPIED_FEEDBACK_DURATION_MS = 3000;

interface CopyAddressButtonProps {
  address: string;
  onCopy: () => void;
}

export const CopyAddressButton: FC<CopyAddressButtonProps> = ({ address, onCopy }) => {
  const { t } = useTranslate();
  const [isCopied, setIsCopied] = useState(false);
  const feedbackTimeout = useRef<number | undefined>(undefined);
  // Clear the copied feedback timer when the visitor leaves the page.
  useEffect(() => () => window.clearTimeout(feedbackTimeout.current), []);

  const copyAddress = async () => {
    const didCopy = await navigator.clipboard.writeText(address).then(
      () => true,
      () => false
    );
    if (!didCopy) {
      return;
    }
    onCopy();
    window.clearTimeout(feedbackTimeout.current);
    setIsCopied(true);
    feedbackTimeout.current = window.setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_DURATION_MS);
  };

  if (isCopied) {
    return (
      <FormTypography
        role="status"
        variant="caption"
        vibe="moderate"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        data-test="org-page-footer-address-copied"
      >
        <FormIcon size="small">
          <Check />
        </FormIcon>
        {t("common", "buttonCopy.copied")}
      </FormTypography>
    );
  }

  return (
    <FormButton
      variant="ghost"
      size="small"
      iconButton
      startIcon={<Copy />}
      aria-label={`${t("common", "copy")}: ${address}`}
      onClick={() => void copyAddress()}
      sx={(theme) => ({
        color: "inherit",
        padding: 0,
        minWidth: 0,
        minHeight: 0,
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
        "&:hover": { backgroundColor: "transparent", opacity: 1 },
      })}
      data-copy-address
      data-test="org-page-footer-address-copy"
    />
  );
};
